import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useTheme } from "@/contexts/ThemeContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownMenuLabel } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import api from "@/lib/api";
import { LogoIcon } from "@/components/Logo";
import GlobalSearch from "@/components/GlobalSearch";
import EmailVerifyBanner from "@/components/EmailVerifyBanner";
import ParentChildSwitcher from "@/components/ParentChildSwitcher";
import PageTitleUpdater from "@/components/PageTitleUpdater";
import {
  LayoutDashboard, BookOpen, Sparkles, ClipboardCheck, GraduationCap,
  ClipboardList, Users2, School as SchoolIcon, MessageSquare, Trophy,
  Bell, BarChart3, User, LogOut, Menu, Moon, Sun, PlusCircle, CheckSquare,
  Wand2, CalendarCheck, Building2, CreditCard, AlertTriangle,
  Megaphone, Calendar as CalendarIcon, Timer, Notebook as NotebookIcon, AlertOctagon,
  Library, FileText, ShieldCheck, TrendingUp, Video, CalendarClock, TrendingDown, Swords
} from "lucide-react";

const NAV = {
  student: [
    { to: "/panel", label: "Panelim", icon: LayoutDashboard },
    { to: "/odevler", label: "Ödevlerim", icon: BookOpen },
    { to: "/takvim", label: "Takvim", icon: CalendarIcon },
    { to: "/ai/ogretmen", label: "AI Öğretmen", icon: Sparkles },
    { to: "/ai/kontrol", label: "AI Ödev Kontrol", icon: CheckSquare },
    { to: "/ai/koc", label: "AI Çalışma Koçu", icon: Wand2 },
    { to: "/meydan-okuma", label: "Meydan Okuma", icon: Swords },
    { to: "/sinavlar", label: "Sınavlarım", icon: ClipboardList },
    { to: "/canli-dersler", label: "Canlı Dersler", icon: Video },
    { to: "/odak", label: "Odak Zamanlayıcı", icon: Timer },
    { to: "/defter", label: "Not Defterim", icon: NotebookIcon },
    { to: "/basarilar", label: "Başarılarım", icon: Trophy },
    { to: "/mesajlar", label: "Mesajlar", icon: MessageSquare },
    { to: "/duyurular", label: "Duyurular", icon: Megaphone },
    { to: "/analitik", label: "Analitik", icon: BarChart3 },
  ],
  teacher: [
    { to: "/panel", label: "Panel", icon: LayoutDashboard },
    { to: "/odevler", label: "Ödevler", icon: BookOpen },
    { to: "/odevler/yeni", label: "Yeni Ödev", icon: PlusCircle },
    { to: "/takvim", label: "Takvim", icon: CalendarIcon },
    { to: "/sinavlar", label: "Sınavlar", icon: ClipboardList },
    { to: "/ai/sinav-uret", label: "AI Sınav Üret", icon: Wand2 },
    { to: "/soru-bankasi", label: "Soru Bankası", icon: Library },
    { to: "/canli-dersler", label: "Canlı Dersler", icon: Video },
    { to: "/gorusmeler", label: "Öğretmen Görüşmeleri", icon: CalendarClock },
    { to: "/yoklama", label: "Yoklama", icon: CalendarCheck },
    { to: "/kullanicilar", label: "Öğrenciler", icon: Users2 },
    { to: "/risk-analizi", label: "Risk Analizi", icon: AlertOctagon },
    { to: "/eksik-konular", label: "Konu Boşlukları", icon: TrendingDown },
    { to: "/veli-uyarilari", label: "Veli Uyarıları", icon: AlertTriangle },
    { to: "/raporlar", label: "Raporlar", icon: FileText },
    { to: "/duyurular", label: "Duyurular", icon: Megaphone },
    { to: "/mesajlar", label: "Mesajlar", icon: MessageSquare },
    { to: "/analitik", label: "Analitik", icon: BarChart3 },
  ],
  parent: [
    { to: "/panel", label: "Panel", icon: LayoutDashboard },
    { to: "/odevler", label: "Çocuğumun Ödevleri", icon: BookOpen },
    { to: "/takvim", label: "Takvim", icon: CalendarIcon },
    { to: "/sinavlar", label: "Sınav Sonuçları", icon: ClipboardList },
    { to: "/canli-dersler", label: "Canlı Dersler", icon: Video },
    { to: "/gorusmeler", label: "Öğretmen Görüşmesi", icon: CalendarClock },
    { to: "/yoklama", label: "Devam Durumu", icon: CalendarCheck },
    { to: "/ai/koc", label: "AI Çalışma Koçu", icon: Wand2 },
    { to: "/mesajlar", label: "Öğretmen Mesajları", icon: MessageSquare },
    { to: "/duyurular", label: "Duyurular", icon: Megaphone },
    { to: "/analitik", label: "Gelişim Raporu", icon: BarChart3 },
  ],
  school_admin: [
    { to: "/panel", label: "Panel", icon: LayoutDashboard },
    { to: "/kullanicilar", label: "Kullanıcılar", icon: Users2 },
    { to: "/siniflar", label: "Sınıflar", icon: SchoolIcon },
    { to: "/odevler", label: "Tüm Ödevler", icon: BookOpen },
    { to: "/sinavlar", label: "Tüm Sınavlar", icon: ClipboardList },
    { to: "/takvim", label: "Takvim", icon: CalendarIcon },
    { to: "/risk-analizi", label: "Risk Analizi", icon: AlertOctagon },
    { to: "/eksik-konular", label: "Konu Boşlukları", icon: TrendingDown },
    { to: "/veli-uyarilari", label: "Veli Uyarıları", icon: AlertTriangle },
    { to: "/raporlar", label: "Raporlar", icon: FileText },
    { to: "/duyurular", label: "Duyurular", icon: Megaphone },
    { to: "/analitik", label: "Okul Analitiği", icon: BarChart3 },
    { to: "/analitik-gelismis", label: "Gelişmiş Analitik", icon: TrendingUp },
    { to: "/denetim", label: "Denetim Kaydı", icon: ShieldCheck },
    { to: "/fiyatlandirma", label: "Abonelik", icon: CreditCard },
  ],
  super_admin: [
    { to: "/panel", label: "Panel", icon: LayoutDashboard },
    { to: "/okullar", label: "Okullar", icon: Building2 },
    { to: "/kullanicilar", label: "Kullanıcılar", icon: Users2 },
    { to: "/siniflar", label: "Sınıflar", icon: SchoolIcon },
    { to: "/takvim", label: "Takvim", icon: CalendarIcon },
    { to: "/risk-analizi", label: "Risk Analizi", icon: AlertOctagon },
    { to: "/veli-uyarilari", label: "Veli Uyarıları", icon: AlertTriangle },
    { to: "/raporlar", label: "Raporlar", icon: FileText },
    { to: "/duyurular", label: "Duyurular", icon: Megaphone },
    { to: "/analitik", label: "Sistem Analitiği", icon: BarChart3 },
    { to: "/analitik-gelismis", label: "Gelişmiş Analitik", icon: TrendingUp },
    { to: "/denetim", label: "Denetim Kaydı", icon: ShieldCheck },
    { to: "/fiyatlandirma", label: "Abonelik", icon: CreditCard },
  ],
};

const ROLE_LABEL = {
  student: "Öğrenci", teacher: "Öğretmen", parent: "Veli",
  school_admin: "Okul Yöneticisi", principal: "Müdür",
  vice_principal: "Müdür Yardımcısı", counselor: "Rehber Öğretmen",
  super_admin: "Süper Yönetici",
};

const ROLE_ACCENT = {
  student: "cyan", teacher: "indigo", parent: "orange",
  school_admin: "emerald", super_admin: "emerald",
  principal: "emerald", vice_principal: "emerald", counselor: "emerald",
};

function NavItem({ to, label, Icon, active }) {
  return (
    <Link
      to={to}
      data-testid={`nav-${to.replace(/\//g, "-").replace(/^-/, "")}`}
      className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-transform duration-150 ease-out
      ${active ? "bg-foreground/[0.06] text-foreground" : "text-muted-foreground hover:text-foreground hover:bg-foreground/[0.04]"}`}
    >
      <span className={`w-1 h-5 rounded-full transition-colors ${active ? "bg-foreground" : "bg-transparent"}`} />
      <Icon size={18} strokeWidth={1.75} className="shrink-0" />
      <span className="truncate">{label}</span>
    </Link>
  );
}

function Sidebar({ role, onNav, user }) {
  const items = NAV[role] || NAV.student;
  const loc = useLocation();
  const accent = ROLE_ACCENT[role] || "indigo";
  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
  return (
    <aside className="h-full flex flex-col p-4 gap-2 border-r border-border bg-card/50" role="complementary" aria-label="Yan menü">
      <Link to="/panel" className="flex items-center gap-2 px-2 py-3" aria-label="Leranio ana sayfa">
        <LogoIcon size={38} rounded="rounded-xl" />
        <div className="leading-tight">
          <div className="font-display font-bold text-lg tracking-tight">Leranio</div>
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Learning Intelligent</div>
        </div>
      </Link>
      <nav className="flex-1 flex flex-col gap-0.5 mt-2 overflow-y-auto" aria-label="Ana gezinme">
        {items.map((it) => (
          <NavItem key={it.to} to={it.to} label={it.label} Icon={it.icon}
            active={loc.pathname === it.to || (it.to !== "/panel" && loc.pathname.startsWith(it.to))}
          />
        ))}
      </nav>
      <div className="pt-4 border-t border-border">
        <Link to="/profil" onClick={onNav} className="flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-foreground/[0.04]">
          <Avatar className="w-8 h-8"><AvatarFallback className={`bg-${accent}-500/15 text-${accent}-600 text-xs font-semibold`}>{initials}</AvatarFallback></Avatar>
          <div className="text-xs min-w-0">
            <div className="font-semibold truncate">{user?.name || "Profil"}</div>
            <div className="text-muted-foreground truncate">{user?.email || "Ayarlar"}</div>
          </div>
        </Link>
      </div>
    </aside>
  );
}

function TopBar() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const accent = ROLE_ACCENT[user?.role] || "indigo";
  const wsRef = useRef(null);
  const pingRef = useRef(null);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/notifications");
        setUnread(data.filter((n) => !n.read).length);
      } catch { /* ignore */ }
    };
    load();

    // WebSocket bağlantısı — gerçek zamanlı bildirimler
    const token = localStorage.getItem("edupilot_token");
    if (!token) return;
    const backend = process.env.REACT_APP_BACKEND_URL || "";
    const wsUrl = backend.replace(/^http/, "ws") + `/api/ws/notifications?token=${encodeURIComponent(token)}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
    } catch { return; }
    wsRef.current = ws;
    ws.onopen = () => {
      pingRef.current = setInterval(() => {
        try { ws.send("ping"); } catch { /* noop */ }
      }, 30000);
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data);
        if (msg.type === "notification") {
          setUnread((u) => u + 1);
          toast(msg.data.title, {
            description: msg.data.body,
            action: { label: "Görüntüle", onClick: () => nav("/bildirimler") },
          });
        }
      } catch { /* ignore */ }
    };
    ws.onclose = () => { if (pingRef.current) clearInterval(pingRef.current); };
    return () => {
      if (pingRef.current) clearInterval(pingRef.current);
      try { ws.close(); } catch { /* noop */ }
    };
    // eslint-disable-next-line
  }, []);

  const initials = (user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header className="sticky top-0 z-40 h-16 border-b border-border glass px-4 md:px-6 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="md:hidden">
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="menu-toggle" aria-label="Menüyü aç"><Menu aria-hidden="true" /></Button>
            </SheetTrigger>
            <SheetContent side="left" className="p-0 w-72">
              <Sidebar role={user?.role} user={user} />
            </SheetContent>
          </Sheet>
        </div>
        <div className="hidden md:flex items-center gap-2">
          <Badge variant="secondary" className={`bg-${accent}-500/10 text-${accent}-600 hover:bg-${accent}-500/15`}>{ROLE_LABEL[user?.role]}</Badge>
        </div>
      </div>
      <GlobalSearch />
      <div className="flex items-center gap-1.5">
        <ParentChildSwitcher />
        <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" aria-label={theme === "dark" ? "Aydınlık tema" : "Karanlık tema"}>
          {theme === "dark" ? <Sun size={18} aria-hidden="true" /> : <Moon size={18} aria-hidden="true" />}
        </Button>
        <Button variant="ghost" size="icon" className="relative" onClick={() => nav("/bildirimler")} data-testid="notifications-btn" aria-label={unread > 0 ? `Bildirimler, ${unread} okunmamış` : "Bildirimler"}>
          <Bell size={18} aria-hidden="true" />
          {unread > 0 && (
            <span className="absolute top-1.5 right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center" aria-hidden="true">{unread > 9 ? "9+" : unread}</span>
          )}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="user-menu-trigger" aria-label="Kullanıcı menüsü" className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-foreground/[0.05]">
              <Avatar className="w-8 h-8"><AvatarFallback className={`bg-${accent}-500/15 text-${accent}-600 text-xs font-semibold`}>{initials}</AvatarFallback></Avatar>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="font-semibold">{user?.name}</div>
              <div className="text-xs text-muted-foreground font-normal">{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav("/profil")} data-testid="menu-profile"><User size={14} className="mr-2" /> Profil</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/2fa")} data-testid="menu-2fa"><ShieldCheck size={14} className="mr-2" /> Güvenlik / 2FA</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/raporlar")}><FileText size={14} className="mr-2" /> Raporlar</DropdownMenuItem>
            <DropdownMenuItem onClick={() => nav("/bildirimler")}><Bell size={14} className="mr-2" /> Bildirimler</DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={logout} data-testid="menu-logout" className="text-red-600 focus:text-red-600"><LogOut size={14} className="mr-2" /> Çıkış</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}

export default function Layout() {
  const { user } = useAuth();
  return (
    <div className="min-h-screen flex bg-background">
      <div className="hidden md:block w-64 shrink-0 sticky top-0 h-screen">
        <Sidebar role={user?.role} user={user} />
      </div>
      <div className="flex-1 min-w-0 flex flex-col">
        <PageTitleUpdater />
        <EmailVerifyBanner />
        <TopBar />
        <main className="flex-1 p-4 md:p-8 lg:p-10 max-w-[1600px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
